import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Input,
  TextArea,
  FileUpload,
  LoadingSpinner,
  PageHeader,
  SaveStatusIndicator,
} from '../components';
import { eventService } from '../services';
import { useAuth } from '../context';
import useAutoSave from '../hooks/useAutoSave';

/**
 * EventCreatePage - Create/Edit Event Form (Screen 06)
 * Design: High-contrast form layout with save draft and publish options
 */
export default function EventCreatePage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const { isAuthenticated } = useAuth();

  const isEditMode = Boolean(eventId);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    fundingGoal: '',
    ticketPrice: '',
    description: '',
    dateTime: '',
    location: '',
    mode: 'open',
  });

  const [isFreeEvent, setIsFreeEvent] = useState(false);
  const [posterFile, setPosterFile] = useState(null);
  const [existingPosterUrl, setExistingPosterUrl] = useState(null);
  const [eventStatus, setEventStatus] = useState(null);

  // House Rules & Party Supplies
  const [houseRules, setHouseRules] = useState([]);
  const [partySupplies, setPartySupplies] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [suppliesOpen, setSuppliesOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Auto-save hook
  const { saveStatus, triggerSave, debouncedSave, autoSaveEventId } = useAutoSave({
    eventId,
    formData,
    houseRules,
    partySupplies,
    isPriceLocked: isEditMode && eventStatus && eventStatus !== 'draft',
  });

  // Navigate to edit URL when auto-save creates a draft
  useEffect(() => {
    if (!isEditMode && autoSaveEventId) {
      navigate(`/host/events/${autoSaveEventId}/edit`, { replace: true });
    }
  }, [autoSaveEventId, isEditMode, navigate]);

  // Auto-upload poster when file selected and event exists
  const handlePosterAutoUpload = useCallback(async (file) => {
    setPosterFile(file);
    const targetId = eventId || autoSaveEventId;
    if (file && targetId) {
      const result = await eventService.uploadPoster(targetId, file);
      if (result.success) {
        setExistingPosterUrl(result.data?.posterUrl || null);
      } else {
        setErrors((prev) => ({ ...prev, poster: 'Poster upload failed' }));
      }
    }
  }, [eventId, autoSaveEventId]);

  // Fetch existing event data if editing
  useEffect(() => {
    if (!isEditMode) return;

    const fetchEvent = async () => {
      setIsLoading(true);
      try {
        const result = await eventService.getEvent(eventId);
        if (result.success && result.data) {
          const event = result.data;

          // Convert datetime to local format for input
          let dateTimeLocal = '';
          if (event.dateTime && new Date(event.dateTime).getFullYear() > 1900) {
            const d = new Date(event.dateTime);
            dateTimeLocal = d.toISOString().slice(0, 16);
          }

          const eventIsFree = event.ticketPriceCents === 0 && event.fundingGoalCents === 0;
          setIsFreeEvent(eventIsFree);
          setFormData({
            title: event.title || '',
            fundingGoal: eventIsFree ? '0' : (event.fundingGoalCents ? (event.fundingGoalCents / 100).toString() : ''),
            ticketPrice: eventIsFree ? '0' : (event.ticketPriceCents ? (event.ticketPriceCents / 100).toString() : ''),
            description: event.description || '',
            dateTime: dateTimeLocal,
            location: event.locationAddress || '',
            mode: event.mode || 'open',
          });
          setExistingPosterUrl(event.posterUrl);
          setEventStatus(event.status);

          // Populate house rules
          if (event.houseRules?.length > 0) {
            setHouseRules(event.houseRules.map((r) => r.text));
            setRulesOpen(true);
          }
          // Populate party supplies (only unclaimed)
          if (event.partySupplies?.length > 0) {
            const unclaimed = event.partySupplies.filter((s) => !s.isClaimed);
            setPartySupplies(unclaimed.map((s) => ({ text: s.text, existingImageUrl: s.imageUrl || null, imageFile: null })));
            setSuppliesOpen(true);
          }
        } else {
          setSubmitError('Failed to load event');
        }
      } catch (err) {
        setSubmitError('Failed to load event');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, isEditMode]);

  // Check if price is locked (non-draft events)
  const isPriceLocked = isEditMode && eventStatus && eventStatus !== 'draft';

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event name is required';
    }

    if (!isFreeEvent) {
      if (!formData.fundingGoal || parseFloat(formData.fundingGoal) <= 0) {
        newErrors.fundingGoal = 'Funding goal must be greater than 0';
      }

      if (!formData.ticketPrice || parseFloat(formData.ticketPrice) <= 0) {
        newErrors.ticketPrice = 'Ticket price must be greater than 0';
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.dateTime) {
      newErrors.dateTime = 'Date and time are required';
    } else {
      const eventDate = new Date(formData.dateTime);
      if (eventDate <= new Date()) {
        newErrors.dateTime = 'Event date must be in the future';
      }
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert dollars to cents
  const dollarsToCents = (dollars) => {
    return Math.round(parseFloat(dollars) * 100);
  };

  // Submit form
  const handleSubmit = async (publish = false) => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build event data
      const filteredRules = houseRules.filter((r) => r.trim());
      const filteredSupplies = partySupplies.filter((s) => s.text.trim());
      const eventData = {
        title: formData.title.trim(),
        fundingGoalCents: isFreeEvent ? 0 : dollarsToCents(formData.fundingGoal),
        description: formData.description.trim(),
        dateTime: new Date(formData.dateTime).toISOString(),
        locationAddress: formData.location.trim(),
        mode: formData.mode,
        houseRules: filteredRules.length > 0 ? filteredRules : [],
        partySupplies: filteredSupplies.length > 0 ? filteredSupplies.map((s) => s.text.trim()) : [],
      };

      // Only include price for draft events
      if (!isPriceLocked) {
        eventData.ticketPriceCents = isFreeEvent ? 0 : dollarsToCents(formData.ticketPrice);
      }

      let result;
      let targetEventId = eventId || autoSaveEventId;

      if (targetEventId) {
        // Update existing event (edit mode or auto-saved draft)
        result = await eventService.updateEvent(targetEventId, eventData);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update event');
        }
      } else {
        // Create new event
        result = await eventService.createEvent(eventData);
        if (!result.success) {
          throw new Error(result.error || 'Failed to create event');
        }
        targetEventId = result.data.id;
      }

      // Upload poster if provided
      let hadUploadErrors = false;
      if (posterFile) {
        const posterResult = await eventService.uploadPoster(targetEventId, posterFile);
        if (!posterResult.success) {
          setSubmitError(`Event saved, but poster upload failed: ${posterResult.error}`);
          hadUploadErrors = true;
        }
      }

      // Upload party supply images if any
      if (result.data?.partySupplies?.length > 0) {
        const supplyImageErrors = [];
        for (let i = 0; i < filteredSupplies.length; i++) {
          if (filteredSupplies[i].imageFile && result.data.partySupplies[i]) {
            const imgResult = await eventService.uploadPartySupplyImage(
              result.data.partySupplies[i].id,
              filteredSupplies[i].imageFile
            );
            if (!imgResult.success) {
              console.error(`Supply image upload failed for index ${i}:`, imgResult.error);
              supplyImageErrors.push(filteredSupplies[i].text);
            }
          }
        }
        if (supplyImageErrors.length > 0) {
          setSubmitError(`Event saved, but image upload failed for: ${supplyImageErrors.join(', ')}`);
          hadUploadErrors = true;
        }
      }

      // If publishing, call publish endpoint
      if (publish && result.data.status === 'draft') {
        const publishResult = await eventService.publishEvent(targetEventId);
        if (!publishResult.success) {
          const errorMsg = publishResult.error?.message || publishResult.error || 'Failed to publish event';
          throw new Error(errorMsg);
        }
      }

      // Don't navigate if there were upload errors — let user see the message
      if (hadUploadErrors) return;

      // Navigate based on mode
      if (isEditMode) {
        navigate(`/host/events/${eventId}`);
      } else {
        navigate(`/host/events/${targetEventId}/share`, {
          state: { published: publish, event: result.data },
        });
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      setSubmitError(error.message || 'Failed to save event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete event
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }
    setIsDeleting(true);
    try {
      const result = await eventService.deleteEvent(eventId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete event');
      }
      navigate('/host');
    } catch (error) {
      setSubmitError(error.message || 'Failed to delete event.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle poster upload error
  const handlePosterError = (errorMessage) => {
    setErrors((prev) => ({
      ...prev,
      poster: errorMessage,
    }));
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            AUTHENTICATION REQUIRED
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">
            Please sign in to {isEditMode ? 'edit' : 'create'} an event.
          </p>
          <Button onClick={() => navigate('/login')}>SIGN IN</Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner label="Loading event..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <PageHeader
        title={isEditMode ? 'Edit Event' : 'Create Event'}
        subtitle={isEditMode ? 'Update your event details' : 'Set up your event details and funding goal'}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => navigate('/host') },
          ...(isEditMode ? [{ label: 'Event', onClick: () => navigate(`/host/events/${eventId}`) }] : []),
          { label: isEditMode ? 'Edit Event' : 'Create Event' },
        ]}
      />

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Auto-save status */}
        <div className="mb-4 h-4">
          <SaveStatusIndicator status={saveStatus} />
        </div>

        {/* Error message */}
        {submitError && (
          <div className="mb-6 p-4 border-2 border-red-600 bg-red-50">
            <p className="font-mono text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {/* Event Name */}
          <Input
            label="Event Name"
            name="title"
            placeholder="Enter event name"
            value={formData.title}
            onChange={handleChange}
            onBlur={triggerSave}
            error={errors.title}
            required
            disabled={isSubmitting}
          />

          {/* Free Event Toggle */}
          <div className="border-2 border-black p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isFreeEvent}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsFreeEvent(checked);
                  if (checked) {
                    setFormData((prev) => ({ ...prev, fundingGoal: '0', ticketPrice: '0' }));
                    setErrors((prev) => ({ ...prev, fundingGoal: null, ticketPrice: null }));
                  } else {
                    setFormData((prev) => ({ ...prev, fundingGoal: '', ticketPrice: '' }));
                  }
                  setTimeout(triggerSave, 0);
                }}
                disabled={isSubmitting || isPriceLocked}
                className="w-5 h-5 border-2 border-black accent-black"
              />
              <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
                FREE EVENT
              </span>
              {isPriceLocked && (
                <span className="font-mono text-xs text-gray-500 normal-case">(locked)</span>
              )}
            </label>
            <p className="mt-2 ml-8 font-mono text-xs text-gray-600">
              No ticket price or funding goal — guests attend for free
            </p>
          </div>

          {/* Funding Goal and Ticket Price - side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
                Funding Goal (P$) <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 font-mono text-gray-500">
                  P$
                </span>
                <input
                  type="number"
                  name="fundingGoal"
                  placeholder="0.00"
                  value={formData.fundingGoal}
                  onChange={handleChange}
                  onBlur={triggerSave}
                  min="0"
                  step="0.01"
                  disabled={isSubmitting || isFreeEvent}
                  className={`
                    w-full pl-10 pr-4 py-3
                    border-2 border-black
                    bg-white text-black
                    font-mono text-sm
                    placeholder:text-gray-400
                    focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2
                    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50
                    ${errors.fundingGoal ? 'border-red-600 focus:ring-red-600' : ''}
                  `}
                />
              </div>
              {errors.fundingGoal && (
                <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
                  {errors.fundingGoal}
                </p>
              )}
            </div>

            <div>
              <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
                Ticket Price (P$) <span className="text-red-600">*</span>
                {isPriceLocked && (
                  <span className="ml-2 text-gray-500 text-xs normal-case">(locked)</span>
                )}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 font-mono text-gray-500">
                  P$
                </span>
                <input
                  type="number"
                  name="ticketPrice"
                  placeholder="0.00"
                  value={formData.ticketPrice}
                  onChange={handleChange}
                  onBlur={triggerSave}
                  min="0"
                  step="0.01"
                  disabled={isSubmitting || isPriceLocked || isFreeEvent}
                  className={`
                    w-full pl-10 pr-4 py-3
                    border-2 border-black
                    bg-white text-black
                    font-mono text-sm
                    placeholder:text-gray-400
                    focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2
                    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50
                    ${errors.ticketPrice ? 'border-red-600 focus:ring-red-600' : ''}
                  `}
                />
              </div>
              {errors.ticketPrice && (
                <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
                  {errors.ticketPrice}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <TextArea
            label="Description"
            name="description"
            placeholder="Describe your event..."
            value={formData.description}
            onChange={handleChange}
            onBlur={triggerSave}
            error={errors.description}
            required
            rows={5}
            maxLength={2000}
            disabled={isSubmitting}
          />

          {/* Date/Time */}
          <div>
            <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
              Date & Time <span className="text-red-600">*</span>
            </label>
            <input
              type="datetime-local"
              name="dateTime"
              value={formData.dateTime}
              onChange={handleChange}
              onBlur={triggerSave}
              disabled={isSubmitting}
              className={`
                w-full px-4 py-3
                border-2 border-black
                bg-white text-black
                font-mono text-sm
                focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2
                disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50
                ${errors.dateTime ? 'border-red-600 focus:ring-red-600' : ''}
              `}
            />
            {errors.dateTime && (
              <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
                {errors.dateTime}
              </p>
            )}
          </div>

          {/* Location */}
          <Input
            label="Location"
            name="location"
            placeholder="Enter event location"
            value={formData.location}
            onChange={handleChange}
            onBlur={triggerSave}
            error={errors.location}
            required
            disabled={isSubmitting}
          />

          {/* Mode Select */}
          <div>
            <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
              Application Mode <span className="text-red-600">*</span>
            </label>
            <div className="inline-flex border-2 border-black">
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, mode: 'curated' }));
                  setTimeout(triggerSave, 0);
                }}
                disabled={isSubmitting}
                className={`
                  px-6 py-3
                  font-mono text-sm font-semibold uppercase tracking-wider
                  transition-colors duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${
                    formData.mode === 'curated'
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-gray-100'
                  }
                `}
              >
                CURATED
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, mode: 'open' }));
                  setTimeout(triggerSave, 0);
                }}
                disabled={isSubmitting}
                className={`
                  px-6 py-3
                  border-l-2 border-black
                  font-mono text-sm font-semibold uppercase tracking-wider
                  transition-colors duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${
                    formData.mode === 'open'
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-gray-100'
                  }
                `}
              >
                OPEN
              </button>
            </div>
            <p className="mt-2 font-mono text-xs text-gray-600">
              {formData.mode === 'curated'
                ? 'You will manually approve or decline each application'
                : 'All applications are automatically accepted'}
            </p>
          </div>

          {/* Poster Upload */}
          <div>
            {existingPosterUrl && !posterFile && (
              <div className="mb-4">
                <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
                  Current Poster
                </label>
                <img
                  src={existingPosterUrl}
                  alt="Event poster"
                  className="max-w-xs border-2 border-black"
                />
              </div>
            )}
            <FileUpload
              label={existingPosterUrl ? 'Replace Poster (Optional)' : 'Event Poster (Optional)'}
              accept="image/*"
              maxSize={10 * 1024 * 1024}
              value={posterFile}
              onChange={handlePosterAutoUpload}
              onError={handlePosterError}
              preview
              aspectRatio="landscape"
            />
            {errors.poster && (
              <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
                {errors.poster}
              </p>
            )}
          </div>

          {/* House Rules Accordion */}
          <div className="border-2 border-black">
            <button
              type="button"
              onClick={() => setRulesOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
                HOUSE RULES {houseRules.length > 0 ? `(${houseRules.filter((r) => r.trim()).length})` : ''}
              </span>
              <span className="font-mono text-sm text-black">{rulesOpen ? '−' : '+'}</span>
            </button>
            {rulesOpen && (
              <div className="px-4 pb-4 border-t-2 border-black space-y-2">
                <p className="font-mono text-xs text-gray-500 mt-3 mb-2">
                  Guests must agree to all rules before applying.
                </p>
                {houseRules.map((rule, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-400 w-6 text-right flex-shrink-0">
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => {
                        const updated = [...houseRules];
                        updated[index] = e.target.value;
                        setHouseRules(updated);
                      }}
                      onBlur={triggerSave}
                      placeholder="Enter rule..."
                      maxLength={500}
                      disabled={isSubmitting}
                      className="flex-1 px-3 py-2 border-2 border-black bg-white text-black font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setHouseRules((prev) => prev.filter((_, i) => i !== index))}
                      disabled={isSubmitting}
                      className="font-mono text-sm text-red-600 hover:text-red-800 px-2 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setHouseRules((prev) => [...prev, ''])}
                  disabled={isSubmitting}
                  className="font-mono text-xs font-semibold uppercase tracking-wider text-black hover:text-gray-600 transition-colors disabled:opacity-50 mt-2"
                >
                  + ADD RULE
                </button>
              </div>
            )}
          </div>

          {/* Party Supplies Accordion */}
          <div className="border-2 border-black">
            <button
              type="button"
              onClick={() => setSuppliesOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
                PARTY SUPPLIES {partySupplies.length > 0 ? `(${partySupplies.filter((s) => s.text.trim()).length})` : ''}
              </span>
              <span className="font-mono text-sm text-black">{suppliesOpen ? '−' : '+'}</span>
            </button>
            {suppliesOpen && (
              <div className="px-4 pb-4 border-t-2 border-black space-y-2">
                <p className="font-mono text-xs text-gray-500 mt-3 mb-2">
                  Guests can claim items they'll bring to the event.
                </p>
                {partySupplies.map((supply, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-400 w-6 text-right flex-shrink-0">
                      {index + 1}.
                    </span>
                    {/* Thumbnail preview or upload button */}
                    <label className="flex-shrink-0 w-8 h-8 border-2 border-black cursor-pointer flex items-center justify-center bg-gray-50 hover:bg-gray-100 overflow-hidden">
                      {supply.imageFile ? (
                        <img
                          src={URL.createObjectURL(supply.imageFile)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : supply.existingImageUrl ? (
                        <img
                          src={supply.existingImageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">+</span>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const updated = [...partySupplies];
                            updated[index] = { ...updated[index], imageFile: file };
                            setPartySupplies(updated);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    </label>
                    <input
                      type="text"
                      value={supply.text}
                      onChange={(e) => {
                        const updated = [...partySupplies];
                        updated[index] = { ...updated[index], text: e.target.value };
                        setPartySupplies(updated);
                      }}
                      onBlur={triggerSave}
                      placeholder="Enter supply item..."
                      maxLength={500}
                      disabled={isSubmitting}
                      className="flex-1 px-3 py-2 border-2 border-black bg-white text-black font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setPartySupplies((prev) => prev.filter((_, i) => i !== index))}
                      disabled={isSubmitting}
                      className="font-mono text-sm text-red-600 hover:text-red-800 px-2 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setPartySupplies((prev) => [...prev, { text: '', imageFile: null, existingImageUrl: null }])}
                  disabled={isSubmitting}
                  className="font-mono text-xs font-semibold uppercase tracking-wider text-black hover:text-gray-600 transition-colors disabled:opacity-50 mt-2"
                >
                  + ADD ITEM
                </button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t-2 border-black">
            {isEditMode ? (
              <Button
                variant="primary"
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'SAVE CHANGES'}
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => handleSubmit(false)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? <LoadingSpinner size="sm" /> : 'SAVE DRAFT'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? <LoadingSpinner size="sm" /> : 'PUBLISH'}
                </Button>
              </>
            )}
          </div>

          {/* Cancel button */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="font-mono text-sm text-gray-600 uppercase tracking-wider hover:text-black transition-colors"
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </button>
          </div>

          {/* Delete button - only for draft events in edit mode */}
          {isEditMode && eventStatus === 'draft' && (
            <div className="pt-4 text-center">
              <button
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting || isDeleting}
                className="font-mono text-sm text-red-600 uppercase tracking-wider hover:text-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'DELETING...' : 'DELETE EVENT'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
